<template lang="pug">
SimpleFormView.bg-white(:loading="viewLoading")
  template(v-slot:title)
    span {{$t(`user.editProfile.${activeView}.title`)}}
  template(v-slot:text)
    span {{$t(`user.editProfile.${activeView}.text`)}}
  template(v-slot:form)
    form.flex-grow-1.d-flex.flex-column.justify-space-between(v-if="user" @submit="onSubmit")
      div.flex-grow-1.pb-7
        EatField(v-if="showField(ProfileFields.NAME)" name="name" v-slot="{ value, handleChange }")
          EatTextInput(:modelValue="value" @update:modelValue="handleChange" icon="$people" :placeholder="$t('form.name.placeholder')")
          EatFieldMessage(name="name" hint="form.hint.required")            
        EatField(v-if="showField(ProfileFields.SURNAME)" name="surname" v-slot="{ value, handleChange }")
          EatTextInput(:modelValue="value" @update:modelValue="handleChange" icon="$surname" :placeholder="$t('form.surname.placeholder')")
          EatFieldMessage(name="surname" hint="form.hint.required")

        EatField(v-if="showField(ProfileFields.PHONE)" name="phone" v-slot="{ value, handleChange }")
          EatTextInput(:modelValue="value" @update:modelValue="handleChange" icon="$mobile" :placeholder="$t('form.phone.placeholder')")
          EatFieldMessage(name="phone" hint="form.hint.required")

        EatField(v-if="showField(ProfileFields.BIRTH_DATE)" name="birthDate" v-slot="{ value, handleChange }")
          EatTextInput(:modelValue="value" @update:modelValue="handleChange" type="date" icon="$calendar" :placeholder="$t('form.phone.placeholder')" pattern="\d{2}-\d{2}-\d{4}")
          EatFieldMessage(name="birthDate" hint="form.hint.required")

        EatField(v-if="showField(ProfileFields.GENDER)" name="gender" v-slot="{ value, handleChange }")
          EatSelect(:options="genders" :modelValue="value" @update:modelValue="handleChange" :placeholder="$t('form.gender.placeholder')")
          EatFieldMessage(name="gender")

      eat-btn.flex-grow-0.bg-primary(
        :loading="isSubmitting"
        type="submit"
      ) {{$t('general.action.save')}}  

  template(v-slot:extra)
    eat-btn(
      :text="true"
      color="text-primary"
      @click="() => goBack(router, route)"
    ) {{$t('general.action.cancel')}} 
      
</template>

<script setup lang="ts">
import { updateProfile as userUpdateProfile } from "@/api/User";
import { goBack } from "@/store/routerHistory";
import { setSnackbar, TypeSnackbar } from "@/store/layout/snackbar";
import { genderTranslations } from "@/utils/genderTranslations";
import { computed, inject, onMounted, onUnmounted, ref, type Ref } from "vue";
import { DateTime } from "luxon";
import { setAppTitle } from "@/store/appTitle";
import { useUser } from "@/store/user";
import type { User } from "@/models";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useForm } from "vee-validate";
import { useApp } from "@/store/app";
import { OptionalUserFields } from "@/models/App";
import { ProfileFields, useProfileValidator } from "@/models/User";
import useLoginLogout from "@/store/loginLogout";
import type { Auth } from "@/plugins/auth";

enum EditProfileViews {
  NAME = "name",
  PHONE = "phone",
  BIRTH_DATE = "birthDate",
  GENDER = "gender",
  COMPLETE = "complete"
}

const { isFieldMandatory, shouldHideField } = useApp();
const { schema, isValid } = useProfileValidator({ gender: isFieldMandatory(OptionalUserFields.GENDER), birthDate: isFieldMandatory(OptionalUserFields.BIRTH_DATE) });
const router = useRouter();
const route = useRoute();
const i18n = useI18n();

setAppTitle(i18n.t("user.editProfile.appTitle").toString());

const activeView = computed(() => {
  if (route.query[ProfileFields.NAME] || route.query[ProfileFields.SURNAME]) return EditProfileViews.NAME;
  if (route.query[ProfileFields.PHONE]) return EditProfileViews.PHONE;
  if (route.query[ProfileFields.BIRTH_DATE]) return EditProfileViews.BIRTH_DATE;
  if (route.query[ProfileFields.GENDER]) return EditProfileViews.GENDER;
  return EditProfileViews.COMPLETE;
});

const viewLoading = ref(true);
const { user, refreshUser } = useUser();
const buttonLoading = ref(false);
const genders = ref(genderTranslations(i18n));

const { values, handleSubmit, isSubmitting, setFieldValue } = useForm({ validationSchema: schema });

onMounted(async () => {
  if(route.query[ProfileFields.GENDER] && shouldHideField(OptionalUserFields.GENDER)) goBack(router, route);
  if(route.query[ProfileFields.BIRTH_DATE] && shouldHideField(OptionalUserFields.BIRTH_DATE)) goBack(router, route);
  viewLoading.value = true;
  user.value ? refreshUser() : await refreshUser();
  if (user.value) {
    setFieldValue('name', user.value.name);
    setFieldValue('surname', user.value.surname);
    setFieldValue('phone', user.value.phones?.length ? user.value.phones[0] : '');
    setFieldValue('gender', user.value.gender);
    setFieldValue('birthDate', user.value.birthDate ? DateTime.fromFormat(user.value.birthDate, "yyyy-MM-dd").toFormat('yyyy-MM-dd') : '');
  }
  viewLoading.value = false;
});

const showField = (field: ProfileFields) => {
  if(field === ProfileFields.GENDER && shouldHideField(OptionalUserFields.GENDER)) return false;
  if(field === ProfileFields.BIRTH_DATE && shouldHideField(OptionalUserFields.BIRTH_DATE)) return false;
  return activeView.value === EditProfileViews.COMPLETE || route.query[field];
}

const updateProfile = async (values: any) => {
  if (!user.value) return;
  buttonLoading.value = true;
  try {
    const newProfile: User = {
      name: values.name || user.value.name || "",
      surname: values.surname || user.value.surname || "",
      email: values.email || user.value.email || "",
      gender: values.gender || user.value.gender || "",
      phones: values.phone ? [values.phone] : user.value.phones || [],
      places: user.value.places || [],
      birthDate: values.birthDate || user.value.birthDate || ""
    };
    user.value = newProfile;
    await userUpdateProfile(newProfile);
  } finally {
    buttonLoading.value = false;
  }
  setSnackbar(i18n.t("user.snackbar.infoUpdated").toString(), TypeSnackbar.SUCCESS);
  const afterLogin = route.query.afterLogin as string;
  if(afterLogin) router.replace({ name: afterLogin });
  else goBack(router, route);
};

const onSubmit = handleSubmit(updateProfile)

onUnmounted(() => {
  const { logout } = useLoginLogout();
  const auth = inject('auth') as Ref<Auth>;
  if(!isValid(values)) logout(auth.value);
})
</script>
